import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { ruleAPI, departmentAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Scale, Plus, Loader2, Trash2, AlertTriangle, Edit, Building2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Rules = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditDeptsOpen, setIsEditDeptsOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    department_id: '',
    rule_type: 'SPEND_LIMIT',
    amount: '',
    period: 'MONTHLY',
    description: ''
  });
  
  const [selectedDepts, setSelectedDepts] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, deptRes] = await Promise.all([
        ruleAPI.listAll(),
        departmentAPI.list()
      ]);
      setRules(rulesRes.data);
      setDepartments(deptRes.data);
    } catch (err) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.department_id || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    try {
      await ruleAPI.create(formData.department_id, {
        rule_type: formData.rule_type,
        amount: parseFloat(formData.amount),
        period: formData.rule_type !== 'ALLOCATION' ? formData.period : null,
        description: formData.description
      });
      toast.success('Rule created successfully');
      setIsCreateOpen(false);
      setFormData({ department_id: '', rule_type: 'SPEND_LIMIT', amount: '', period: 'MONTHLY', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create rule');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await ruleAPI.delete(ruleId);
      toast.success('Rule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const openEditDepts = (rule) => {
    setEditingRule(rule);
    setSelectedDepts(rule.department_ids || (rule.department_id ? [rule.department_id] : []));
    setIsEditDeptsOpen(true);
  };

  const handleSaveDepts = async () => {
    if (selectedDepts.length === 0) {
      toast.error('At least one department must be selected');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/rules/${editingRule.rule_id}/departments`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ department_ids: selectedDepts })
      });
      
      if (res.ok) {
        toast.success('Rule departments updated');
        setIsEditDeptsOpen(false);
        setEditingRule(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update departments');
    } finally {
      setSaving(false);
    }
  };

  const toggleDept = (deptId) => {
    setSelectedDepts(prev => 
      prev.includes(deptId) 
        ? prev.filter(d => d !== deptId)
        : [...prev, deptId]
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const getRuleTypeColor = (type) => {
    switch (type) {
      case 'SPEND_LIMIT': return 'text-amber-600 bg-amber-100';
      case 'RECEIVE_LIMIT': return 'text-blue-600 bg-blue-100';
      case 'ALLOCATION': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="rules-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Rules Management</h1>
            <p className="text-gray-500 mt-1">Configure spending limits and allocations for departments</p>
          </div>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="add-rule-btn">
                <Plus className="w-4 h-4" /> Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Create Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-600">Initial Department</Label>
                  <Select value={formData.department_id} onValueChange={(val) => setFormData({...formData, department_id: val})}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-300">
                      {departments.map(d => <SelectItem key={d.department_id} value={d.department_id} className="text-gray-900">{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">You can add more departments after creation</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600">Rule Type</Label>
                  <Select value={formData.rule_type} onValueChange={(val) => setFormData({...formData, rule_type: val})}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-300">
                      <SelectItem value="SPEND_LIMIT" className="text-gray-900">Spend Limit</SelectItem>
                      <SelectItem value="RECEIVE_LIMIT" className="text-gray-900">Receive Limit</SelectItem>
                      <SelectItem value="ALLOCATION" className="text-gray-900">Fund Allocation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.rule_type !== 'ALLOCATION' && (
                  <div className="space-y-2">
                    <Label className="text-gray-600">Period</Label>
                    <Select value={formData.period} onValueChange={(val) => setFormData({...formData, period: val})}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-300">
                        <SelectItem value="DAILY" className="text-gray-900">Daily</SelectItem>
                        <SelectItem value="WEEKLY" className="text-gray-900">Weekly</SelectItem>
                        <SelectItem value="MONTHLY" className="text-gray-900">Monthly</SelectItem>
                        <SelectItem value="YEARLY" className="text-gray-900">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-gray-600">Amount (₹)</Label>
                  <Input type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} placeholder="10000" className="bg-white border-gray-300 text-gray-900 font-['JetBrains_Mono']" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600">Description (Optional)</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Rule description" className="bg-white border-gray-300 text-gray-900" />
                </div>
                <Button onClick={handleCreate} className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Create Rule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {rules.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rules.map((rule) => {
              const utilization = rule.amount > 0 ? (rule.current_usage / rule.amount) * 100 : 0;
              const isApproaching = utilization >= 80;
              const deptNames = rule.department_names || (rule.department_name ? [rule.department_name] : []);
              
              return (
                <Card key={rule.rule_id} className="glass-card">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${getRuleTypeColor(rule.rule_type)}`}>
                        {rule.rule_type.replace('_', ' ')}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDepts(rule)} 
                          className="text-gray-500 hover:text-blue-600"
                          title="Edit departments"
                          data-testid={`edit-depts-${rule.rule_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(rule.rule_id)} className="text-gray-500 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Departments */}
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {deptNames.map((name, idx) => (
                          <Badge key={idx} variant="outline" className="border-blue-200 text-blue-700 text-xs">
                            <Building2 className="w-3 h-3 mr-1" />
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-4">
                      {rule.period ? `${rule.period} limit` : 'Total allocation'}
                      {rule.description && ` • ${rule.description}`}
                    </p>
                    
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-500">Used</span>
                        <span className="text-gray-900 font-['JetBrains_Mono']">{formatCurrency(rule.current_usage)} / {formatCurrency(rule.amount)}</span>
                      </div>
                      <Progress value={Math.min(utilization, 100)} className={`h-2 ${isApproaching ? 'bg-red-100' : 'bg-gray-100'}`} />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Remaining</span>
                      <span className="text-sm font-['JetBrains_Mono'] text-blue-600">{formatCurrency(rule.remaining_budget)}</span>
                    </div>
                    
                    {isApproaching && (
                      <div className="mt-4 flex items-center gap-2 text-amber-600 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Approaching limit</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Scale className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No rules configured yet</p>
              <Button onClick={() => setIsCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-2" /> Add First Rule
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Edit Departments Dialog */}
        <Dialog open={isEditDeptsOpen} onOpenChange={setIsEditDeptsOpen}>
          <DialogContent className="bg-white border-gray-200 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Edit Rule Departments
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-4">
                Select which departments this rule applies to. The rule limit will be shared across all selected departments.
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {departments.map(dept => (
                  <label 
                    key={dept.department_id} 
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedDepts.includes(dept.department_id) 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Checkbox
                      checked={selectedDepts.includes(dept.department_id)}
                      onCheckedChange={() => toggleDept(dept.department_id)}
                      data-testid={`dept-checkbox-${dept.department_id}`}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{dept.name}</p>
                      <p className="text-xs text-gray-500">Balance: {formatCurrency(dept.balance)}</p>
                    </div>
                  </label>
                ))}
              </div>
              
              <div className="mt-4 text-sm text-gray-500">
                {selectedDepts.length} department{selectedDepts.length !== 1 ? 's' : ''} selected
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsEditDeptsOpen(false)} className="border-gray-300">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveDepts} 
                className="bg-blue-600 hover:bg-blue-700 text-white" 
                disabled={saving || selectedDepts.length === 0}
                data-testid="save-depts-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} 
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Rules;
